// Builds the Sentry span name for a sampled API request: METHOD + a path with
// every id-shaped segment collapsed to `:id`, so we never leak a concrete
// UUID/portal-id/numeric-id into a span name (which is high cardinality and
// would otherwise mint a new-looking "route" per request).
const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGIT_SEGMENT = /^\d+$/;
// Portal/resource ids used across the API are base62 (alphanumeric),
// 9-12 characters. A plain route segment (verb, resource name) is real
// English and, at this length, is consistently either all-lowercase or all
// one case with no digits — so either a digit or a mix of upper/lower case
// is a safe signal this is a random id rather than a word: a digit rules out
// "companies"/"workspace", and mixed case rules out an all-letter id like a
// product-store shortId ("aBcDeFgHi") that would otherwise sneak through.
const BASE62_LENGTH_SEGMENT = /^[0-9a-zA-Z]{9,12}$/;
const HAS_DIGIT = /[0-9]/;
const HAS_LOWERCASE = /[a-z]/;
const HAS_UPPERCASE = /[A-Z]/;

const isBase62IdSegment = (segment: string): boolean =>
  BASE62_LENGTH_SEGMENT.test(segment) &&
  (HAS_DIGIT.test(segment) ||
    (HAS_LOWERCASE.test(segment) && HAS_UPPERCASE.test(segment)));

const isIdLikeSegment = (segment: string): boolean =>
  UUID_SEGMENT.test(segment) ||
  ALL_DIGIT_SEGMENT.test(segment) ||
  isBase62IdSegment(segment);

export const normalizeApiPathForSpanName = (path: string): string =>
  path
    .split('/')
    .map((segment) => (segment && isIdLikeSegment(segment) ? ':id' : segment))
    .join('/');

export const buildApiSpanName = (method: string, path: string): string =>
  `${method.toUpperCase()} ${normalizeApiPathForSpanName(path)}`;
